import { create } from 'zustand';
import { supabase } from '../supabase';
import { RoughBodyPiece } from '../types';
import { useAuthStore } from './useAuthStore';
import { useOfflineQueue } from './useOfflineQueue';

interface RoughBodyState {
    roughBodies: RoughBodyPiece[];
    loading: boolean;
    error: string | null;

    // Reads
    fetchRoughBodies: () => Promise<void>;
    findRoughBodyByQrCode: (qrCode: string) => Promise<RoughBodyPiece | null>;

    // Station Actions
    updateStationStatus: (id: string, stage: 'cutting' | 'sewing', action: 'start' | 'complete', payload: any) => Promise<void>;

    // Delivery
    markAsDelivered: (id: string, notes?: string) => Promise<void>;
}

export const useRoughBodyStore = create<RoughBodyState>((set, get) => ({
    roughBodies: [],
    loading: false,
    error: null,

    fetchRoughBodies: async () => {
        const userProfile = useAuthStore.getState().userProfile;
        if (!userProfile?.factory_id) return;

        set({ loading: true, error: null });
        try {
            const { data, error } = await supabase
                .from('rough_body_pieces')
                .select('*, styles(*), sample_requests(*)');

            if (error) throw error;

            const filteredData = (data as any[]).filter(
                (piece) => piece.sample_requests?.factory_id === userProfile.factory_id
            );

            set({ roughBodies: filteredData || [] });
        } catch (e: any) {
            console.error('Fetch Rough Bodies Error:', e);
            set({ error: e.message });
        } finally {
            set({ loading: false });
        }
    },

    findRoughBodyByQrCode: async (qrCode: string) => {
        try {
            const { data, error } = await supabase
                .from('rough_body_pieces')
                .select('*, styles(*), sample_requests(*)')
                .eq('qr_code', qrCode)
                .maybeSingle();

            if (error) throw error;
            return data as any;
        } catch (e) {
            console.error('Find Rough Body Error:', e);
            return null;
        }
    },

    updateStationStatus: async (id, stage, action, payload) => {
        // Offline check
        if (!navigator.onLine) {
            await useOfflineQueue.getState().addToQueue('roughBodyUpdateStatus', { id, stage, action, payload });
            return;
        }
        const userProfile = useAuthStore.getState().userProfile;
        if (!userProfile) return;

        const currentPiece = get().roughBodies.find((p) => p.id === id);
        const prevStatus = currentPiece?.status || 'Requested';

        // OPTIMISTIC UI: Update memory immediately so UI feels instantaneous
        const timestamp = new Date().toISOString();
        let updateData: any = { updated_at: timestamp };
        let newStatus = '';

        if (stage === 'cutting') {
            if (action === 'start') {
                updateData.cutting_started_at = timestamp;
                updateData.status = 'Cutting Started';
                newStatus = 'Cutting Started';
            } else if (action === 'complete') {
                updateData.cutting_completed_at = timestamp;
                updateData.status = 'Cutting Completed';
                newStatus = 'Cutting Completed';
            }
        }
        else if (stage === 'sewing') {
            if (action === 'start') {
                updateData.sewing_started_at = timestamp;
                updateData.status = 'Sewing Started';
                newStatus = 'Sewing Started';

                // Tailor Assignment Logic
                if (payload.assigned_tailor_name) {
                    updateData.assigned_tailor_name = payload.assigned_tailor_name;
                    updateData.assigned_tailor_id = payload.assigned_tailor_id; // New field
                }
            } else if (action === 'complete') {
                updateData.sewing_completed_at = timestamp;
                updateData.status = 'Sewing Completed';
                // (Rough body might be done, but keep as Sewing Completed to surface to user)
                newStatus = 'Sewing Completed';
            }
        }

        // Apply optimistic update
        set(state => ({
            roughBodies: state.roughBodies.map(rb =>
                rb.id === id ? { ...rb, ...updateData } : rb
            )
        }));

        try {
            const { error } = await supabase
                .from('rough_body_pieces')
                .update(updateData)
                .eq('id', id);

            if (error) throw error;

            // LOGGING FOR DASHBOARD RECENT ACTIVITY
            if (userProfile.factory_id && newStatus) {
                let stationName = stage === 'cutting' ? 'Rough Body Cutting' :
                    stage === 'sewing' ? 'Rough Body Sewing' :
                        'Rough Body QC';

                // General Log into piece_scan_log
                await supabase.from('piece_scan_log').insert({
                    factory_id: userProfile.factory_id,
                    sample_piece_id: id,
                    scanned_by: userProfile.id,
                    station: stationName,
                    previous_status: prevStatus,
                    new_status: newStatus,
                    scanned_at: timestamp
                });

                // Extra Specific log for Assignment into tailor_assignments
                if (stage === 'sewing' && action === 'start' && payload.assigned_tailor_name) {
                    await supabase.from('tailor_assignments').insert({
                        factory_id: userProfile.factory_id,
                        rough_body_piece_id: id,
                        tailor_name: payload.assigned_tailor_name,
                        assigned_at: timestamp,
                        piece_status: 'Sewing Started'
                    });
                }
            }

        } catch (e: any) {
            console.error('Update Rough Body Error:', e);
            // Revert optimistic UI on Error
            await get().fetchRoughBodies();
            throw e;
        }
    },

    markAsDelivered: async (id, notes) => {
        // Offline check
        if (!navigator.onLine) {
            await useOfflineQueue.getState().addToQueue('roughBodyMarkDelivered', { id, notes });
            return;
        }
        try {
            const { error } = await supabase
                .from('rough_body_pieces')
                .update({
                    status: 'Completed',
                    completed_at: new Date().toISOString(),
                    delivered_at: new Date().toISOString(),
                    pattern_master_notes: notes
                })
                .eq('id', id);

            if (error) throw error;
            await get().fetchRoughBodies();
        } catch (e) {
            console.error(e);
        }
    }
}));
